/**
 * 형제 노드 간 표시 순서를 위한 최소 lexorank(fractional indexing) 유틸. base62(`0-9A-Za-z`)
 * 문자셋을 쓴다 — 각 문자의 ASCII 코드가 이미 이 순서대로 정렬돼 있어(0x30-0x39 < 0x41-0x5A <
 * 0x61-0x7A), 문자열을 그대로 `ORDER BY sort_key`로 정렬해도 의도한 순서와 일치한다.
 *
 * 리밸런싱(모든 rank가 한 자리에 몰려 문자열이 계속 길어지는 상황 완화)은 v1 범위 밖이다 —
 * 재정렬은 저빈도 조작이라 실무에서 rank 문자열이 감당 못 할 만큼 길어질 가능성은 낮다고 보고,
 * 필요해지면 후속 작업으로 별도 도입한다.
 *
 * **가변 길이라 "틈이 소진"되는 일은 정상 입력에서는 없다**(2026-07-18, `lexorank` npm 패키지 —
 * Jira 원조 알고리즘 — 검토 중 확인한 차이점). 그 패키지는 고정 길이 버킷 방식이라 `"no gap left"`
 * 예외가 있고 호출부가 형제 전체를 재배치(rebalance)해야 한다. 이 구현은 자리수를 한 자리 더 내려가면
 * 중간값을 만들 수 있다(두 실수 사이에 항상 실수가 있는 것과 같은 원리) — `hiDigit - loDigit > 1`을
 * 만나면 즉시 반환하고, 못 만나면 한 자리 더 내려가며, `hiExhausted`가 켜진 뒤로는 `hiDigit -
 * loDigit`이 최소 `BASE - 0 = 64 > 1`이라 다음 자리에서 종료된다. 대가는 같은 좁은 틈에 반복적으로
 * 끼워 넣을수록 rank 문자열이 길어진다는 것뿐이다(`sort_key VARCHAR(255)` 한도 내에서는 실무상 도달
 * 불가능한 수준).
 *
 * **예외 하나: `lo`와 `hi`가 끝의 '0' 문자만 다른 경우**(예: `'a'`·`'a0'`, `null`·`'0'`)는 문자열로는
 * `lo < hi`이지만 짧은 쪽을 '0'(자리 값 0)으로 채워 비교하면 두 값이 같아 **그 사이에 들어갈 값이 없다**.
 * 이 구현은 이 입력에서 자리마다 `hiDigit === loDigit`로 끝없이 내려가 무한 루프(`result`도 계속 증가)였다 —
 * 이제 `NoRankGapError`를 던진다. `rankBetween`이 만든 키는 중간 자리 값을 쓰므로 '0'으로 끝나지 않지만,
 * v5(레거시)가 같은 컬럼에 쓴 키 등 외부 값은 보장되지 않는다. 호출부는 이 오류를 "삽입 불가"로 처리한다.
 *
 * 이력: 2026-07-20 PR 9bfcd1585(`OrgTeam` Phase A)에서 `libs/backend/spinoff-platform`에 처음
 * 구현됐으나, 이후 `OrgTeam`→`OrgTeamVersion` SCD-2 재설계에서 통째로 삭제되고 재도입되지 않았다.
 * 2026-08-15 sbe-schedule 장소 계층(`Location`) 재설계에서 되살려 도메인 무관 공용 lib으로
 * 승격 — team 쪽 재도입, `sbe-commerce`의 `Catalog`/`Offer` lexorank 전환에도 그대로 재사용 가능.
 */

// ':'(58)와 '|'(124)를 base62 뒤에 추가한 base64 알파벳 — ASCII 순서를 유지해야 하므로 각각
// '9'(57) 바로 뒤, 'z'(122) 바로 뒤에 온다. sbe-api-v5가 자기 lexorank npm 패키지(Jira 스타일)로
// 써넣는 이웃 rank를 rankBetween이 만나도 크래시하지 않도록 문자셋만 넓힌 것 — 알고리즘은 그대로다
// (apps/schoolbell-e/plans/active/2026-08-18-team-order-priority-lexorank-divergence.md 참고).
const ALPHABET = '0123456789:ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz|';
const BASE = ALPHABET.length; // 64

function digitAt(rank: string, index: number): number {
  if (index >= rank.length) return 0;
  const ch = rank[index];
  const value = ALPHABET.indexOf(ch);
  if (value === -1) {
    throw new Error(`[lexorank] invalid character "${ch}" in rank "${rank}"`);
  }
  return value;
}

/** 두 rank 사이에 끼워 넣을 값이 없다 — `lo`와 `hi`가 끝의 '0' 문자만 다른 경우(위 헤더 설명). */
export class NoRankGapError extends Error {
  constructor(lo: string | null, hi: string | null) {
    super(`[lexorank] no rank exists between lo(${lo}) and hi(${hi})`);
    this.name = 'NoRankGapError';
  }
}

/**
 * `lo`(없으면 문자열의 시작, 즉 모든 자리 0)와 `hi`(없으면 문자열의 끝, 즉 모든 자리
 * BASE — 실제 존재하는 최댓값보다 항상 크다고 취급) 사이의 새 rank 문자열을 만든다.
 * `lo`가 주어지면 항상 `lo < result`, `hi`가 주어지면 항상 `result < hi`.
 * **목록 맨 뒤에 붙일 때(`hi`가 `null`)는 이 함수가 아니라 {@link rankAfter}를 쓴다** — `hi`가 없으면 키가 7번마다 한 글자씩 길어진다.
 * `lo >= hi`면 `Error`, 둘 사이에 들어갈 값이 없으면(끝의 '0'만 다름) `NoRankGapError`를 던진다.
 */
export function rankBetween(lo: string | null, hi: string | null): string {
  if (lo !== null && hi !== null && lo >= hi) {
    throw new Error(`[lexorank] lo(${lo}) must be strictly less than hi(${hi})`);
  }

  // 두 값이 어느 자리에서도 갈라지지 않는 길이 — 이 길이를 넘겨서도 같으면(끝의 '0' 채움만 다름) 틈이 없다.
  const maxLen = Math.max(lo?.length ?? 0, hi?.length ?? 0);
  let result = '';
  let i = 0;
  // hi가 lo의 진짜 다음 자리 값(loDigit+1)과 자리마다 일치하는 동안은, 그 뒤로는 hi가 더 이상
  // 제약을 주지 못한다(어차피 loDigit로 시작하고 그 뒤에 뭐가 오든 hi보다 작다) — 그 지점부터는
  // hiExhausted로 전환해 hi를 무시하고 lo보다만 크게 만든다.
  let hiExhausted = hi === null;

  for (;;) {
    const loDigit = lo === null ? 0 : digitAt(lo, i);
    const hiDigit = hiExhausted ? BASE : hi === null ? BASE : digitAt(hi, i);

    if (hiDigit === loDigit && i >= maxLen) {
      throw new NoRankGapError(lo, hi);
    }

    if (hiDigit - loDigit > 1) {
      const midDigit = loDigit + Math.floor((hiDigit - loDigit) / 2);
      result += ALPHABET[midDigit];
      return result;
    }

    // hiDigit - loDigit === 1 (또는 lo/hi가 이 자리에서 우연히 같아 0인 경우는 위 조건에서 이미
    // 걸러지지 않으므로 이 분기에 못 옴 — hiDigit===loDigit이면 다음 자리로 그대로 내려간다).
    if (hiDigit === loDigit) {
      result += ALPHABET[loDigit];
      i++;
      continue;
    }

    // hiDigit === loDigit + 1: 이 자리는 loDigit 그대로 쓰고, 그 다음 자리부터는 hi가 더 이상
    // 제약이 아니다(loDigit로 시작하는 모든 문자열은 hi보다 작다).
    result += ALPHABET[loDigit];
    i++;
    hiExhausted = true;
  }
}

/**
 * 목록 **맨 뒤**에 붙일 rank — `lo`는 지금 마지막 형제의 rank이고, 형제가 하나도 없으면 `null`이다(그러면 첫 rank). **맨 뒤에 붙일 때는 `rankBetween(lo, null)`이 아니라 이 함수를 쓴다.**
 * 이 규칙은 `tools/ci/lint-rank-append.mjs`가 강제한다.
 *
 * 왜 따로 두는가: `rankBetween(lo, null)`은 `hi`가 없으면 자리값을 `BASE` 쪽으로 절반씩 좁혀 가다(32 → 48 → 56 → 60 → 62 → 63) 막히면 **글자를 하나 더 붙인다** —
 * 7번 붙일 때마다 키가 한 글자씩 길어져 한 목록에 약 1,500개를 붙이면 `sort_key VARCHAR(255)`를 넘긴다(실측: 1,500번째에 251자). 한 학교 학생 2000명을 한 단체에 만드는
 * 규모에서 `OrgMemberVersion.sortKey`가 이 한도에 걸려 생성이 실패했다. 팀·장소처럼 형제가 적은 목록도 같은 방식으로 붙이므로 한 함수로 모은다.
 *
 * 방식: 뒤에서부터 최댓값(`'|'`)이 아닌 첫 자리를 찾아 1 올리고 그 뒤를 잘라낸다(`'a|'` → `'b'`). 전부 최댓값이면 글자 하나(`'1'`)를 붙인다. 결과는 항상 `lo`보다 크고
 * 끝이 `'0'`이 아니라(끝이 `'0'`이면 `rankBetween`이 틈을 못 찾는 경우가 생긴다 — 위 `NoRankGapError`) 이후 두 키 사이 삽입도 `rankBetween`으로 그대로 된다. 길이는
 * 약 63번 붙일 때 한 글자씩만 늘어(2000개 ≈ 32자) 한도(255자)까지 약 1만 5천 개 여유가 있다. 이미 길어진 키 뒤에 붙이면 길이가 늘지 않고 오히려 줄어든다.
 *
 * **알파벳 밖 문자는 던지지 않는다**(레거시 v5가 같은 컬럼에 쓴 키 호환) — 그런 문자를 만나면 그 뒤에 글자를 이어 붙인다. 어떤 문자열이든 `lo + '1'`은 `lo`보다 크므로 순서는
 * 항상 맞고, `rankBetween(lo, null)`이 첫 글자만 보고 끝나 이런 키에서도 던지지 않던 동작보다 나빠지지 않는다.
 *
 * `rankBetween`은 건드리지 않는다 — 기존 호출부(두 키 사이 삽입)와 그 스펙이 고정한 값이 바뀌지 않는다.
 */
export function rankAfter(lo: string | null): string {
  if (lo === null) return firstRank();
  for (let i = lo.length - 1; i >= 0; i--) {
    const digit = ALPHABET.indexOf(lo[i]);
    if (digit === -1) break;
    if (digit < BASE - 1) return lo.slice(0, i) + ALPHABET[digit + 1];
  }
  return lo + ALPHABET[1];
}

/** 목록의 맨 앞에 넣을 때 쓰는 초기 rank. */
export function firstRank(): string {
  return rankBetween(null, null);
}
